import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Space, Row, Col, Breadcrumb, Modal, message,
    Typography, Select, DatePicker, Input, Tag, Descriptions, Tabs, Form
} from "antd";
import {
    HomeOutlined, DollarOutlined, FilterOutlined, ClearOutlined,
    SearchOutlined, CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const tabItems = [
    { key: "pending", label: "Pending Settlement" },
    { key: "settled", label: "Settled" },
    { key: "rejected", label: "Rejected" },
];

const PaymentSettle = () => {
    const [payments, setPayments] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [search, setSearch] = useState("");
    const [filterAccount, setFilterAccount] = useState(undefined);
    const [filterDates, setFilterDates] = useState([]);
    const [applied, setApplied] = useState({});

    const [settleItem, setSettleItem] = useState(null);
    const [settleForm] = Form.useForm();
    const [settleLoading, setSettleLoading] = useState(false);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await api.get("/masterdropdown/listof_activepaymentaccounts.php");
            setAccounts(res.data.dataset || []);
        } catch {
            message.error("Failed to load payment accounts.");
        }
    }, []);

    const fetchPayments = useCallback(async (page = 1, pageSize = 10, tab = activeTab, filters = {}) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/payment/list.php", {
                page,
                limit: pageSize,
                tab,
                payacc_id: filters.payacc_id || 0,
                search: filters.search || "",
                start_date: filters.start_date || "",
                end_date: filters.end_date || "",
            });
            setPayments(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch payments.");
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchAccounts();
        fetchPayments(1, 10, activeTab, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            payacc_id: filterAccount,
            start_date: filterDates?.[0] ? filterDates[0].format("YYYY-MM-DD") : "",
            end_date: filterDates?.[1] ? filterDates[1].format("YYYY-MM-DD") : "",
        };
        setApplied(filters);
        fetchPayments(1, pagination.pageSize, activeTab, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterAccount(undefined);
        setFilterDates([]);
        setApplied({});
        fetchPayments(1, pagination.pageSize, activeTab, {});
    };

    const handleSettle = async (action) => {
        const comment = action === "reject" ? (settleForm.getFieldValue("comment") || "").trim() : "";
        setSettleLoading(true);
        try {
            const res = await api.post("/expenses/payment/settle.php", {
                exppay_id: settleItem.exppay_id,
                status: action === "settle" ? "Settled" : "Rejected",
                comment,
            });
            if (res.data.status === "success") {
                message.success(res.data.message);
                setSettleItem(null);
                settleForm.resetFields();
                fetchPayments(1, pagination.pageSize, activeTab, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to settle payment.");
        } finally {
            setSettleLoading(false);
        }
    };

    const columns = [
        {
            title: "Payment Ref", dataIndex: "exppay_ref", key: "exppay_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Expense Ref", dataIndex: "exp_ref", key: "exp_ref",
            render: (v) => (v ? <Tag color="blue">{v}</Tag> : "-"),
        },
        {
            title: "Account", dataIndex: "account_name", key: "account_name",
            render: (v) => v || "-",
        },
        {
            title: "Paid Amount", dataIndex: "paid_amount", key: "paid_amount", align: "right",
            render: (v) => <strong>LKR {parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
        },
        {
            title: "Release Date", dataIndex: "payment_release_date", key: "payment_release_date", align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Payment Ref No.", dataIndex: "payment_reference", key: "payment_reference",
            render: (v) => v || "-",
        },
        {
            title: "Status", dataIndex: "payment_status", key: "payment_status", align: "center",
            render: (s) => {
                const color = s === "Settled" ? "green" : s === "Rejected" ? "red" : "gold";
                return <Tag color={color} style={{ fontWeight: 600 }}>{s}</Tag>;
            },
        },
        {
            title: "Created By", dataIndex: "creator_name", key: "creator_name",
            render: (v) => v || "-",
        },
        {
            title: "Action", key: "action", align: "center", fixed: "right", width: 110,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={record.payment_status === "Pending" ? <CheckOutlined /> : <SearchOutlined />}
                    onClick={() => { settleForm.resetFields(); setSettleItem(record); }}
                >
                    {record.payment_status === "Pending" ? "Settle" : "View"}
                </Button>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Expenses' },
                                { title: 'Payment Settle' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <DollarOutlined /> Payment Settle
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Settle payments that have been released</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div style={{ padding: "16px 16px 0" }}>
                        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                    </div>

                    {/* Advanced filter bar */}
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Payment ref, expense ref, ref no."
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Account:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterAccount}
                                    onChange={setFilterAccount}
                                    placeholder="All Accounts"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {accounts.map((a) => <Option key={a.value} value={Number(a.value)}>{a.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Release Date Range:</div>
                                <RangePicker value={filterDates} onChange={setFilterDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <Row gutter={8} className="fluid-filter-actions">
                                    <Col span={12}>
                                        <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary" block>
                                            Apply
                                        </Button>
                                    </Col>
                                    <Col span={12}>
                                        <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary" block>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={payments}
                            rowKey="exppay_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pg) => fetchPayments(pg.current, pg.pageSize, activeTab, applied)}
                            loading={loading}
                            scroll={{ x: 1100 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Settle modal */}
            <Modal
                title={`Settle Payment - ${settleItem?.exppay_ref || ""}`}
                open={!!settleItem}
                onCancel={() => setSettleItem(null)}
                footer={null}
                width={600}
            >
                {settleItem && (
                    <>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="Expense Ref" span={2}>
                                {settleItem.exp_ref ? <Tag color="blue">{settleItem.exp_ref}</Tag> : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Expense Title" span={2}>{settleItem.exp_title || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Account">{settleItem.account_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Release Date">{dayjs(settleItem.payment_release_date).format("YYYY-MM-DD")}</Descriptions.Item>
                            <Descriptions.Item label="Paid Amount" span={2}>
                                <Typography.Text strong style={{ color: "#1677ff" }}>
                                    LKR {parseFloat(settleItem.paid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Payment Ref No." span={2}>{settleItem.payment_reference || "-"}</Descriptions.Item>
                            {settleItem.payment_status !== "Pending" && (
                                <>
                                    <Descriptions.Item label="Checked By">{settleItem.checked_name || "-"}</Descriptions.Item>
                                    <Descriptions.Item label="Checked At">
                                        {settleItem.checked_dt ? dayjs(settleItem.checked_dt).format("YYYY-MM-DD HH:mm") : "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Comment" span={2}>{settleItem.checked_comment || "-"}</Descriptions.Item>
                                </>
                            )}
                        </Descriptions>

                        {settleItem.payment_status === "Pending" && (
                            <Form form={settleForm} layout="vertical" style={{ marginTop: 16 }}>
                                <Form.Item label="Comment (required for rejection)" name="comment">
                                    <TextArea rows={2} maxLength={100} placeholder="Optional for settling, required for rejecting" />
                                </Form.Item>
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                    <Button danger icon={<CloseOutlined />} loading={settleLoading} onClick={() => handleSettle("reject")}>
                                        Reject
                                    </Button>
                                    <Button type="primary" icon={<CheckOutlined />} loading={settleLoading} onClick={() => handleSettle("settle")}>
                                        Settle
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </>
                )}
            </Modal>
        </Layout>
    );
};

export default PaymentSettle;
